import React, { useEffect, useState } from 'react';
import { IManagePersonCascadingPanelsProps, ManagePersonCascadingPanelIds } from './manage-person-panels.models';
import { FetchType } from '../../../../../enums';
import { useAppActions, useAppState } from '../../../../../overmind';
import { Permissions } from '../../../../../models/overmind/permissions';
import { TagType } from '../../../../../enums/tag';
import { TagListItem } from '../../../../../models/tag';
import { ManagePersonPermissionsCascadingPanels } from './manage-person-permissions-panels/manage-person-permissions-cascading-panels';
import { PagePath } from '../../../../../navigation/navigation.enums';
import { ManagePersonFocusPanel } from './manage-person-focus-panel/manage-person-focus-panel';
import { CascadingPanel, CascadingPanels, useCascadingPanelsConnector } from '@keplerco/core';
import { GetUserDto } from '../../../../../models/view/employee';

export function ManagePersonCascadingPanels(props: IManagePersonCascadingPanelsProps): JSX.Element {
  const actions = useAppActions();
  const { companyVariables } = useAppState();

  const { openPanelIds, next, previous, close } = useCascadingPanelsConnector();

  const [person, setPerson] = useState<GetUserDto>();
  const [permissions, setPermissions] = useState<Permissions>();
  const [tags, setTags] = useState<TagListItem[]>();

  async function getPerson(learnerSlug: string): Promise<GetUserDto | undefined> {
    const data = await actions.getPerson(learnerSlug);
    setPerson(data);
    return data;
  }

  async function getPermissions(learnerSlug: string | undefined) {
    if (!learnerSlug) return;
    const data = await actions.getPermissions(learnerSlug);
    setPermissions(data);
  }

  async function getTags(personSlug: string | undefined) {
    if (!companyVariables.slug || !personSlug) return;
    const data = await actions.getAssigneeTags({ companySlug: companyVariables.slug, entitySlug: personSlug, tagType: TagType.User });
    setTags(data);
  }

  useEffect(() => {
    next(ManagePersonCascadingPanelIds.Sections);
  }, []);

  useEffect(() => {
    async function getData(): Promise<void> {
      if (!props.learnerSlug) return;

      actions.startLoader({ path: PagePath.userManagementPeople, type: FetchType.DialogFetching });

      await getPerson(props.learnerSlug);
      await getPermissions(props.learnerSlug);
      await getTags(props.learnerSlug);

      actions.stopLoader(PagePath.userManagementPeople);
    }

    getData();
  }, [props.learnerSlug]);

  return (
    <CascadingPanels
      openPanelIds={openPanelIds}
      onClosePanel={id => {
        close(id);
        if (id === ManagePersonCascadingPanelIds.Sections || openPanelIds.length <= 1) props.onCancel();
      }}
    >
      <CascadingPanel id={ManagePersonCascadingPanelIds.Sections}>
        <ManagePersonFocusPanel
          person={person}
          permissions={permissions}
          tags={tags}
          onClickSection={section => next(section)}
          onCancel={props.onCancel}
          onSubmit={async learnerSlug => {
            actions.startLoader({ path: PagePath.userManagementPeople, type: FetchType.DialogFetching });
            await getPerson(learnerSlug);
            await getPermissions(learnerSlug);
            props.onSubmit();
            actions.stopLoader(PagePath.userManagementPeople);
          }}
        />
      </CascadingPanel>

      <CascadingPanel id={ManagePersonCascadingPanelIds.Permissions}>
        <ManagePersonPermissionsCascadingPanels
          person={person}
          permissions={permissions}
          onSave={async () => {
            if (!person) return;
            actions.startLoader({ path: PagePath.userManagementPeople, type: FetchType.DialogFetching });
            await getPermissions(person.learnerSlug);
            actions.stopLoader(PagePath.userManagementPeople);
          }}
          onCancel={() => previous()}
        />
      </CascadingPanel>
    </CascadingPanels>
  );
}

